/**
 * The header of ONE release's page — 10.2, and what is known about it
 * without opening a version: status, product, how many versions it holds.
 *
 * A release is a container and is rendered as one: this header, then its
 * versions. There is no release-level change log, documents or files —
 * everything hangs off a version (10.2.0.27), so there is one place to look
 * and one place to upload. Admin controls here are about the container:
 * New version, Edit, Publish, Delete.
 */
import { Box, Button, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Folder, Pencil, Plus, Rocket, Trash2 } from "lucide-react";
import type { JSX } from "react";

import { RELEASE_STATUS_COLOR, statusLabel } from "./release-status.js";
import type { ReleaseDTO } from "./releases-api.js";

interface Props {
  line: ReleaseDTO;
  versionCount: number;
  /** False when the row is an orphan version standing in for its release —
   *  nothing can be added to or removed from a release that is not here. */
  hasLine: boolean;
  canEdit: boolean;
  onAddVersion: () => void;
  onEdit: () => void;
  onPublish: () => void;
  onDelete: () => void;
}

export function ReleaseHeader({
  line,
  versionCount,
  hasLine,
  canEdit,
  onAddVersion,
  onEdit,
  onPublish,
  onDelete,
}: Props): JSX.Element {
  return (
    <Box
      data-testid={`release-header-${line.version}`}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        px: 2.5,
        py: 2,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: (t) => alpha(t.palette.primary.main, 0.02),
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 44,
          height: 44,
          borderRadius: 1,
          flexShrink: 0,
          color: "primary.main",
          bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
        }}
      >
        <Folder size={22} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography
            component="h3"
            sx={{
              fontFamily: (t) => t.typography.h6.fontFamily,
              fontSize: 24,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              lineHeight: 1.2,
            }}
          >
            Release {line.version}
          </Typography>
          <Chip
            size="small"
            variant={line.status === "released" ? "filled" : "outlined"}
            color={RELEASE_STATUS_COLOR[line.status] ?? "default"}
            label={statusLabel(line.status)}
            sx={{ height: 20 }}
          />
          <Chip
            size="small"
            variant="outlined"
            label={`${versionCount} version${versionCount === 1 ? "" : "s"}`}
            sx={{ height: 20 }}
          />
          {!hasLine && (
            <Tooltip title="This version's release is not on this page — it stands on its own.">
              <Chip size="small" variant="outlined" label="Unfiled" sx={{ height: 20 }} />
            </Tooltip>
          )}
        </Box>
        <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.5 }}>
          {line.product_name ? `${line.product_name} · ` : ""}
          {line.description ?? "Every version shipped as part of this release."}
        </Typography>
      </Box>
      {canEdit && hasLine && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<Plus size={16} />}
            onClick={onAddVersion}
            aria-label={`Add version to ${line.version}`}
          >
            New version
          </Button>
          <IconButton size="small" color="inherit" aria-label={`Edit ${line.version}`} onClick={onEdit}>
            <Pencil size={16} />
          </IconButton>
          {!line.released && (
            <Tooltip title="Publish this release">
              <IconButton
                size="small"
                color="inherit"
                aria-label={`Publish ${line.version}`}
                onClick={onPublish}
              >
                <Rocket size={16} />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            size="small"
            aria-label={`Delete ${line.version}`}
            onClick={onDelete}
            sx={{ color: "error.main" }}
          >
            <Trash2 size={16} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}
